import React, { useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import LazyLoad from 'react-lazyload'
import Head from 'next/head'
import trackError from '@helpers/trackError'
import {
  isBrowser,
  getLink,
  getCurrencyCode,
  getShopLocale
} from '@helpers/env'
import { getValueFromObject } from '@helpers/utils'
import CustomHead from '@components/Head'
import useSizingWidgetData from '@helpers/hooks/useSizingWidgetData'
import { PRODUCTION_PATH, STAGING_PATH } from '@consts/index'
import {
  Product,
  ProductByType,
  ProductMeta,
  ProductVariant
} from 'types/product'
import {
  BenefitsProps,
  ContentfulDocument,
  StatementSectionProps
} from 'types/contentful/graphQlApi'
import { YotpoStarRatingProps } from '@components/product/yotpoRatings/YotpoStarRatings'
import { ProductsBreakdownProps } from '@components/ui/ProductsBreakdown'
import styles from '../styles.module.scss'
import YotpoReviews from '../yotpoRatings/YotpoReviews'
import ProductContext from '../context'
import ProductContainer from '../index'
import { getProductInventoryByProductType } from '@api/inventory'
import { getProductsWithUpdatedVariants } from '@helpers/filters/updateVariants'
import StatementSection from '@components/pages/components/StatementSection'
import Benefits from '@components/ui/Benefits'
import PageSection from '@components/ui/PageSection'

const SizingWidget = dynamic(() => import('../SizingWidget'))
const ProductProductsBreakdown = dynamic(
  () => import('../ProductProductsBreakdown')
)
const Loader = dynamic(() => import('../../Loading/PageLoading'))

const basePath =
  process.env.API_ENV === 'production' ? PRODUCTION_PATH : STAGING_PATH

function makeProductSchema(
  product: ProductListProduct,
  productUrl: string,
  meta: { description: string },
  ratings: YotpoStarRatingProps['starRatings']
) {
  return {
    '@context': 'http://schema.org',
    '@type': 'Product',
    description: meta.description,
    name: product.product_type,
    image: product.image?.src,
    brand: product.product_feed?.brand,
    sku: product.product_feed?.product_id,
    mpn: product.product_feed?.product_id,
    color: product.title,
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: getCurrencyCode(),
      price: Number(product.price) || null,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        returnPolicyCategory:
          'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 200,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn'
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: getShopLocale() === 'US' ? '15.00' : '4.95',
          currency: getCurrencyCode()
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: getShopLocale()
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 2,
            unitCode: 'd'
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 2,
            maxValue: 4,
            unitCode: 'd'
          }
        }
      }
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: ratings?.average_score,
      ratingCount: ratings?.total_review
    }
  }
}

type ProductDiscount = {
  title: string
  description: string
}

export type ProductListProduct = Product & {
  discount: ProductDiscount
  byline: string
  variants?: ProductVariant[]
}

export type ProductsList = (ProductByType & {
  products: ProductListProduct[]
})[]

export type ProductListProps = {
  productsList: ProductsList
  productsBreakdown: ProductsBreakdownProps | null
  meta: ProductMeta
  starRatings: YotpoStarRatingProps['starRatings'] | null
  careCopy: ContentfulDocument | null
  productIds: number[]
  productTypes: string[]
  productStatementSection: StatementSectionProps | null
  productBenefits: BenefitsProps | null
}

const ProductList = ({
  productsBreakdown = null,
  starRatings = null,
  careCopy,
  productsList,
  productBenefits,
  productStatementSection
}: ProductListProps) => {
  const { query, asPath } = useRouter()
  const productHandle = Array.isArray(query.productHandle)
    ? query.productHandle[0]
    : query.productHandle

  const [variantExist, setVariantExist] = useState(false)
  const [products, setProducts] = useState(productsList)

  const productUrl = `${basePath}${asPath}`

  const flatProducts = products.flatMap(product => product.products)
  const selectedProduct =
    flatProducts.find(product => {
      return product.handle.toLowerCase() === productHandle
    }) || flatProducts[0]

  const { sizingWidgetData } = useSizingWidgetData({
    isPreview: query.preview === 'true',
    handle: selectedProduct?.handle,
    productCategory: selectedProduct?.product_feed?.category
  })

  const productContext = useMemo(
    () => ({
      discount: selectedProduct?.discount || {},
      careCopy,
      tabs: products,
      variantExist
    }),
    [selectedProduct?.discount, careCopy, products, variantExist]
  )

  const fetchInventoryByProductTypes = async () => {
    const productTypes = products.map(
      product => product.products[0].product_type
    )

    const promises = productTypes.map(productType =>
      getProductInventoryByProductType(productType)
    )
    const responses = await Promise.all(promises)

    const inventoryItems = responses.flatMap(
      inventory => inventory.productInventoryByType.items
    )

    return { productInventoryByType: { items: inventoryItems } }
  }

  useEffect(() => {
    const fetchVariants = async () => {
      try {
        const { productInventoryByType } = await fetchInventoryByProductTypes()

        const productsWithVariants = products.map(productType => ({
          ...productType,
          products:
            getProductsWithUpdatedVariants(
              productType.products,
              productInventoryByType.items
            ) || []
        }))
        setProducts(productsWithVariants)
        setVariantExist(true)
      } catch (e) {
        const variantsError = new Error('Product fetchVariants failed')
        const error = e instanceof Error ? e : variantsError
        trackError(variantsError, { error })
      }
    }
    fetchVariants()
  }, [])

  const shouldShowProductReviews = Number(starRatings?.total_review) > 0
  const shouldShowProductsBreakdown =
    productsBreakdown && productsBreakdown?.categories.items.length > 0
  const shouldShowSizingWidget = Object.keys(sizingWidgetData).length > 0

  if (query && !selectedProduct) {
    if (Object.keys(query).length === 0 && isBrowser()) {
      global.window.location.href = getLink('/pages/404')
    }
    return <Loader />
  }

  const metaSeo = {
    title:
      getValueFromObject(selectedProduct, 'meta.seo_title', undefined) ||
      `${selectedProduct?.title} - ${selectedProduct?.product_type}`,
    description:
      getValueFromObject(selectedProduct, 'meta.seo_description', undefined) ||
      selectedProduct?.byline,
    image: {
      url: selectedProduct?.image?.src,
      width: selectedProduct?.image?.width,
      height: selectedProduct?.image?.height
    }
  }

  return (
    <div className={styles.productContainer}>
      <Head>
        <script
          key={`jobJSON-${selectedProduct?.id}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              makeProductSchema(
                selectedProduct,
                productUrl,
                metaSeo,
                starRatings
              )
            )
          }}
        />
      </Head>
      <CustomHead
        title={metaSeo.title}
        desc={metaSeo.description}
        image={metaSeo.image}
        includeNoFollow={false}
      />
      <ProductContext.Provider value={productContext}>
        <ProductContainer
          selectedProduct={selectedProduct}
          byline={selectedProduct?.byline || ''}
          productHandle={productHandle || ''}
          productColor={selectedProduct?.title || ''}
          variantExist={variantExist}
          starRatings={starRatings}
        />
      </ProductContext.Provider>
      {productStatementSection && (
        <PageSection>
          <StatementSection {...productStatementSection} />
        </PageSection>
      )}
      {productBenefits && (
        <PageSection>
          <Benefits
            backgroundColor={productBenefits.backgroundColor}
            benefits={productBenefits.benefits}
          />
        </PageSection>
      )}
      {shouldShowSizingWidget && (
        <PageSection>
          <SizingWidget
            sizingWidgetData={sizingWidgetData}
            productType={selectedProduct.product_type || ''}
            productCategory={selectedProduct?.product_feed?.category || ''}
          />
        </PageSection>
      )}
      {shouldShowProductsBreakdown && (
        <LazyLoad once>
          <PageSection>
            <ProductProductsBreakdown
              productsBreakdown={productsBreakdown}
              productOptions={selectedProduct?.options || []}
              productType={selectedProduct?.product_type || ''}
            />
          </PageSection>
        </LazyLoad>
      )}
      {shouldShowProductReviews && (
        <LazyLoad height={200} once>
          <PageSection>
            <div className={styles.yotpoRatings__Wrapper}>
              <YotpoReviews
                productId={String(selectedProduct?.id)}
                productTitle={selectedProduct?.title || ''}
                productImage={selectedProduct?.image?.src || ''}
                byline={selectedProduct?.byline}
              />
            </div>
          </PageSection>
        </LazyLoad>
      )}
    </div>
  )
}

export default ProductList
